import { useState } from 'react'
import { useParams, Link, Navigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import {
  ArrowLeftIcon,
  PencilIcon,
  CubeIcon,
  TagIcon,
  CogIcon,
  BeakerIcon,
} from '@heroicons/react/24/outline'
import { GET_PRODUCT_VARIANT } from '../graphql/product-queries'
import { ProductVariant } from '../types/product'
import LoadingSpinner from '../components/LoadingSpinner'
import { usePermissions } from '../hooks/usePermissions'
import { PERMISSIONS } from '../lib/permissions'
import VariantBasicInfoTab from '../components/VariantBasicInfoTab'
import VariantOptionGroupsTab from '../components/VariantOptionGroupsTab'
import VariantBundleGroupsTab from '../components/VariantBundleGroupsTab'
import VariantRecipeTab from '../components/costing/VariantRecipeTab'

const ProductVariantDetailPage = () => {
  const { productId, variantId } = useParams<{ productId: string; variantId: string }>()
  const [searchParams] = useSearchParams()
  const initialTab = searchParams.get('tab') as 'basic' | 'options' | 'bundles' | 'recipe' | null
  const [activeTab, setActiveTab] = useState<'basic' | 'options' | 'bundles' | 'recipe'>(
    initialTab ?? 'basic'
  )
  const { hasPermission } = usePermissions()
  const showCogs = hasPermission(PERMISSIONS.COSTING_MANAGE)

  const { data, loading, error } = useQuery(GET_PRODUCT_VARIANT, {
    variables: { id: parseInt(variantId!) },
    skip: !variantId
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !data?.productVariant) {
    return <Navigate to={`/products/${productId}`} replace />
  }

  const variant: ProductVariant = data.productVariant

  const tabs = [
    { id: 'basic', name: 'Basic Info', icon: CubeIcon },
    { id: 'options', name: 'Option Groups', icon: TagIcon },
    { id: 'bundles', name: 'Grouped Meals', icon: CogIcon },
    ...(showCogs ? [{ id: 'recipe', name: 'Recipe & COGS', icon: BeakerIcon }] : []),
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to={`/products/${productId}`}
            className="flex items-center text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Product
          </Link>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">{variant.name}</h1>
            {/* Statiegeld belongs next to the price wherever the price is
                stated: a tariff you cannot see without opening the edit form is
                a tariff nobody can audit before switching it on. */}
            <p className="mt-1 text-sm text-gray-500">
              {variant.product?.name} • €{(variant.price_cents / 100).toFixed(2)}
              {(variant.deposit_cents ?? 0) > 0 && (
                <span className="text-gray-400">
                  {' '}+ €{((variant.deposit_cents ?? 0) / 100).toFixed(2)} statiegeld
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Link
            to={`/products/${productId}/variants/${variantId}/edit`}
            className="btn-secondary"
          >
            <PencilIcon className="h-4 w-4 mr-2" />
            Edit Variant
          </Link>
        </div>
      </div>

      {/* Status Badge */}
      <div className="flex items-center space-x-4">
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
          variant.active 
            ? 'bg-green-100 text-green-800' 
            : 'bg-red-100 text-red-800'
        }`}>
          {variant.active ? 'Active' : 'Inactive'}
        </span>
        {variant.sku && (
          <span className="text-sm text-gray-500">SKU: {variant.sku}</span>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as 'basic' | 'options' | 'bundles' | 'recipe')}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-5 w-5 mr-2" />
                {tab.name}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'basic' && (
          <VariantBasicInfoTab variant={variant} />
        )}
        {activeTab === 'options' && (
          <VariantOptionGroupsTab 
            variant={variant} 
            productId={parseInt(productId!)} 
          />
        )}
        {activeTab === 'bundles' && (
          <VariantBundleGroupsTab
            variant={variant}
            productId={parseInt(productId!)}
          />
        )}
        {activeTab === 'recipe' && showCogs && (
          <VariantRecipeTab variant={variant} />
        )}
      </div>
    </div>
  )
}

export default ProductVariantDetailPage

